import { FormInput, Popover, toast } from '@systemeio/ui'
import {
  BLOGS_API,
  createCategory,
  deleteCategory,
  updateCategory,
} from 'modules/blogs/api/blog-api'
import { useBlogCategories } from 'modules/blogs/hooks/use-blog-categories'
import {
  BlogCategoriesCreateInterface,
  BlogCategoriesEditInterface,
  BlogCategoriesInterface,
} from 'modules/blogs/types/blog-categories-interface'
import { BlogInterface } from 'modules/blogs/types/blog-interface'
import React, { useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { buildPath } from 'shared/utils/build-path'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { QuestionMarkIcon } from '@systemeio/ui/icons'

interface BlogPagesProps {
  blog?: BlogInterface
  blogId: string
}

const defaultValues: BlogCategoriesCreateInterface = {
  name: '',
  path: '',
  description: '',
  seoDescription: '',
  seoTitle: '',
}

const defaultErrors: BlogCategoriesEditInterface = {
  name: '',
  path: '',
  seoDescription: '',
  description: '',
  seoTitle: '',
}

const BlogCategories = ({ blog, blogId }: BlogPagesProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination(true)

  const { blogCategories, blogCategoriesMutate, blogCategoriesCounter } = useBlogCategories({
    limit: limit,
    pagination: pagination,
    id: blogId as string,
  })

  const updateData = useUpdateData({
    api: `${BLOGS_API}/${blogId}/categories`,
    setPagination,
  })

  const [errors, setErrors] = useState<BlogCategoriesEditInterface>(defaultErrors)

  const onCreate = async (data: BlogCategoriesCreateInterface) => {
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          const newData: BlogCategoriesCreateInterface = {
            name: data.name,
            path: data.path,
            description: data.description || '',
            seoTitle: data.seoTitle || '',
            seoDescription: data.seoDescription || '',
          }
          await createCategory(blog?.id, newData)
          await updateData(true, blogCategories)
        }
      }, setErrors)
      toast.success(t('dashboard.blog.categories.created'))
    } catch (e) {
      throw e
    }
  }

  const onEdit = async (category: BlogCategoriesInterface, data: BlogCategoriesInterface) => {
    try {
      await fetchWithErrors(async () => {
        if (blog) {
          const newData: BlogCategoriesEditInterface = {
            name: data.name,
            path: data.path,
            seoDescription: data.seoDescription || '',
            seoTitle: data.seoTitle || '',
            description: data.description || '',
          }
          const response = await updateCategory(category?.id, newData)
          await updateData(false, undefined, {
            mutationData: blogCategories,
            onMutate: async () => {
              await blogCategoriesMutate(data => {
                if (data) {
                  return {
                    ...data,
                    items: data.items.map(el => {
                      if (el.id === category.id) {
                        return { ...response.data }
                      } else {
                        return el
                      }
                    }),
                  }
                }
              }, false)
            },
          })
        }
      }, setErrors)
    } catch (e) {
      throw e
    }
  }

  const onRemove = async (data: BlogCategoriesInterface) => {
    try {
      await deleteCategory(data.id)
      await updateData(false, blogCategories)
    } catch (e) {
      throw e
    }
  }

  const onModalClose = () => setErrors(defaultErrors)

  const columns: ColumnDefinitionInterface<BlogCategoriesInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 30,
    },
    {
      key: 'path',
      widthPercent: 30,
      header: t('dashboard.blog.categories.title'),
      onRender: blogCategory => {
        return (
          <div className="truncate">
            <a rel="noreferrer" className="primary-link w-full" href={blogCategory.viewUrl}>
              {blogCategory.path}
            </a>
          </div>
        )
      },
    },
    {
      key: 'postsCount',
      widthPercent: 10,
      header: t('dashboard.blog.categories.total_blog_posts'),
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 30,
      onRender: blogCategory => (
        <span>
          {dateTimeFormat(blogCategory.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  return (
    <ListingLayout
      title={t('dashboard.blog.categories.title_plural')}
      counter={blogCategoriesCounter}
      data={blogCategories}
      type={'compact'}
      createData={{
        onCreate: onCreate,
        onAfterLeave: onModalClose,
        requiredFields: ['name', 'path'],
        fullCaption: t('dashboard.blog.categories.create_new_category'),
        needToast: false,
        defaultValues,
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange, state, onChangeState) => (
              <FormInput
                required
                key={'name-create'}
                value={value}
                onChange={e =>
                  onChangeState({
                    ...state,
                    name: e.target.value,
                    path: buildPath(e.target.value),
                  })
                }
                label={t('global.title')}
                error={errors.name}
              />
            ),
          },
          {
            field: 'path',
            onRender: (value, onChange) => (
              <FormInput
                required
                key={'path-create'}
                label={t('dashboard.blog.categories.path')}
                value={value}
                onChange={e => onChange(e.target.value)}
                error={errors.path}
              />
            ),
          },
          {
            field: 'description',
            onRender: (value, onChange) => (
              <FormInput
                key={'description-create'}
                label={t('global.description')}
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                error={errors.description}
              />
            ),
          },
          {
            field: 'seoTitle',
            onRender: (value, onChange) => (
              <FormInput
                key={'seo-title-create'}
                label={
                  <div className={'flex items-center gap-1'}>
                    Seo title
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.blog.categories.seo_title_description')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                error={errors.seoTitle}
              />
            ),
          },
          {
            field: 'seoDescription',
            onRender: (value, onChange) => (
              <FormInput
                key={'seo-description-create'}
                label={
                  <div className={'flex items-center gap-1'}>
                    Seo description
                    <Popover
                      popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                      label={t('dashboard.blog.categories.seo_description_description')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                error={errors.seoDescription}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.blog.categories.title')}
        actions={{
          settings: {
            onEdit: (instance, data) => onEdit(instance, data),
            onModalClose,
            fullCaption: t('dashboard.blog.categories.edit_categories'),
            editable: [
              {
                field: 'name',
                onRender: (value, onChange, state, _, __, onChangeState) => (
                  <FormInput
                    required
                    key={'name-create'}
                    value={value}
                    onChange={e => {
                      onChangeState({
                        ...state,
                        name: e.target.value,
                        path: e.target.value
                          .replaceAll(' ', '-')
                          .replace(/[^a-zA-Z|\-_\d+]/g, '')
                          .toLowerCase(),
                      })
                    }}
                    label={t('global.title')}
                    error={errors.name}
                  />
                ),
              },
              {
                field: 'path',
                onRender: (value, onChange) => (
                  <FormInput
                    required
                    key={'path-create'}
                    label={t('dashboard.blog.categories.path')}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.path}
                  />
                ),
              },
              {
                field: 'description',
                onRender: (value, onChange) => (
                  <FormInput
                    key={'description-create'}
                    label={t('global.description')}
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    error={errors.description}
                  />
                ),
              },
              {
                field: 'seoTitle',
                onRender: (value, onChange) => (
                  <FormInput
                    key={'seo-title-create'}
                    label={
                      <div className={'flex items-center gap-1'}>
                        Seo title
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.blog.categories.seo_title_description')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    error={errors.seoTitle}
                  />
                ),
              },
              {
                field: 'seoDescription',
                onRender: (value, onChange) => (
                  <FormInput
                    key={'seo-description-create'}
                    label={
                      <div className={'flex items-center gap-1'}>
                        Seo description
                        <Popover
                          popoverClassName={'max-w-[200px] sm:max-w-[350px]'}
                          label={t('dashboard.blog.categories.seo_description_description')}
                        >
                          {show => (
                            <QuestionMarkIcon
                              className={`${
                                show ? 'fill-brand-primary' : 'fill-fill-primary'
                              } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                            />
                          )}
                        </Popover>
                      </div>
                    }
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    error={errors.seoDescription}
                  />
                ),
              },
            ],
          },
          remove: { onChange: onRemove },
        }}
        data={blogCategories}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
      />
    </ListingLayout>
  )
}

export default BlogCategories
